/**
 * ④PJ の変更一覧 — 要求 │ ステージ │ 業務フロー │ 業務 │ 業務ルール │ 画面 の 6 レーン。
 *
 * **1 件の案件が仕様のどこを変えたかを、要求から業務ルール・画面まで 1 画面で辿る**（合意
 * `pj-changes.html`。画面のレーンは Dhik 2026-09-28「画面は業務ルールの横に置いて」）。ステージから
 * 業務ルールまでの 4 レーンは業務全体像とまったく同じ部品（`LaneBoard`）で、違いは「この案件が触った
 * 要素だけを出す」という絞り込みだけである。
 *
 * **レーンに載らない変更（用語・マスター）はこの画面に出さない**（Dhik 2026-09-29「PJ変更一覧の横断する
 * 変更の情報は丸ごと消してほしい」）。前はタブ「横断的に関わるデータ」に 1 件ずつ出していた。差分の読取
 * （`getPjDiff`）そのものは差分の画面と MCP が使うので残している。
 *
 * **4 レーンに出すのは案件の変更の行を持つ要素だけである**（Dhik 2026-09-29「PJの変更一覧で出すものを
 * changeに紐付けられた変更のみとする」）。配下だけが変わったステージ・業務フロー・業務は出さない
 * （`withoutDescendantPjLaneChanges`）。画面のレーンは部品だけが変わった画面も出す — 部品のレーンは無く、部品の
 * 変更を表すのは持ち主の画面のカードしかない。ただしそのカードに「配下に変更」の札も枠も付けない（Dhik
 * 2026-09-29「PJモードの業務マップでも配下に変更を出さず、色もつけないでほしい」の「でも」＝業務マップ以外も。
 * `pjScreenItemsOf` が変更なしに畳む）。
 *
 * **カードに配下の件数も所属も書かない**（Dhik 2026-09-29「カードの下に出ている変わった配下の数の情報を
 * すべて消して」「業務のカードにある業務フロー、アクターの情報を削って」）。要求・画面のカードは見出し
 * だけ、業務のカードの下段はドメインだけにする。
 *
 * **業務全体像の読取を 2 本引く**（設計書 `api.html#a7`）。案件を当てた姿（`after`）には
 * 案件が消した要素が居ないので、master の姿からそれを拾って削除の印を付ける。画面の名前も同じ理由で
 * 画面一覧を 2 本（案件を当てた姿と master）引く。
 *
 * **画面のレーンは案件で変わった画面を出す。** 部品の変更は持ち主の画面に寄せてあり（読取の
 * `screenChanges`）、業務を選ぶとその業務を操作する部品を持つ画面に絞られる。
 *
 * **案件を選ぶ場所はこの画面に持たない。** 案件の一覧（`/pjs`）が案件を選ぶ入口で、選んだ
 * 案件がこの画面のパス（`/pjs/$pjNumber`）そのものである。案件のレーンは置かない。
 *
 * **案件の変更と案件の要求は外から受け取る** — ここで空の既定値を置くと、読取がまだ来て
 * いないことと「案件が何も触っていない」ことが見分けられなくなる。
 *
 * **欄ごとの見比べはこの画面に置かない。** 前は帯に差分の件数と「差分を見る」を置いていたが、
 * Dhik の指示（2026-09-28「PJ変更一覧の差分の件数と差分を見るのコンポーネントは不要なので消して」）で
 * 外した。差分の画面（`/pjs/$pjNumber/diff`）のルートは残している。
 *
 * **見出し → PJ 憲章の中身 → 変更のレーン列 の順に描き、タブは置かない**（Dhik 2026-09-29「PJ憲章は
 * タイトルの下に出してほしい」）。同日いったん憲章と横断的に関わるデータをタブの奥へ移したが、憲章は
 * 見出しの下へ戻し、横断的に関わるデータは消したので、残る中身は変更の 1 つだけになった。1 つしか無い
 * タブは押す先が無いので並びごと置かない。憲章の節の見出しは置かない（ページの見出しが案件を名指しし、
 * 欄名が中身を言う）。憲章の読取はレーン列の 6 本と別に扱い、失敗しても憲章の中身だけを失敗にする
 * （`PjCharterSection`）。案件の状態（未反映 ／ 反映済み）は見出しの `title` に載せる。
 * **カードは名前と変更の印だけを見せる。** 業務フローの型・業務のドメイン・未翻訳はカードの `title`、レーンの
 * 件数は見出しの `title`、「詳細」と要求の「背景」はカードに指を載せたときだけ出す。業務ルールの実装先と
 * 空のレーンの案内は置かない。
 *
 * カードは業務マップと同じ看板形式で描く（`LaneBoard`）。カードを押すと右のレーンが絞られ、別の
 * 画面へはカード右上の「詳細」で行く。要求の why は要求のカード右下の「背景」からモーダルで開き、
 * 開いても絞り込みは動かない。業務フローのレーンは業務マップと同じ並び（`orderFlowsByStage`）。
 *
 * **要求のカードの見出しは要求の文の上限の字数で切る**（Dhik 2026-09-28「日付ブロックの要求の説明が
 * 長すぎて見てられない」）。上限は書込だけに効くので、それより前に書かれた長い文が読取に残る。
 * 見出しは先頭を上限の字数で切って「…」を付け（`truncateRequirementStatement`）、全文は背景の
 * モーダルの見出しで読む。上限以下の文は切らない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更一覧の見出しの下に PJ 憲章の中身を出し、その下に案件が直接変えた要素と変わった画面を 6 レーンの看板形式（名前と変更の印だけ）で描き、要求の背景のモーダルを添える。
 * @graph-connects usePjCharter [calls] 案件の憲章の取得
 * @graph-connects PjCharterSection [calls] PJ 憲章の節
 * @graph-connects useBusinessMap [calls] 案件を当てた姿と master の姿の取得
 * @graph-connects usePjLaneChanges [calls] 変わったレーン 4 型の取得
 * @graph-connects withoutDescendantPjLaneChanges [calls] 変わったレーン 4 型から直接の変更だけを残す
 * @graph-connects usePjScreenChanges [calls] 部品を持ち主に寄せた、案件で変わった画面の取得
 * @graph-connects useListScreens [calls] 画面の名前（案件を当てた姿と master）の取得
 * @graph-connects LaneBoard [calls] レーン列の描画
 * @graph-connects selectPjLanes [calls] 案件が触った要素だけの 5 レーンの中身
 * @graph-connects orderFlowsByStage [calls] 業務フローを業務マップと同じ並びにする
 * @graph-connects PjRequirementWhyModal [calls] 要求の背景のモーダル
 * @graph-connects truncateRequirementStatement [calls] 要求のカードの見出しの切り詰め
 */
import { truncateRequirementStatement } from '@cortex/domain-spec-console';
import type {
  PjChangeRow,
  PjLaneChange,
  PjRequirement,
  PjScreenChange,
} from '@cortex/domain-spec-console';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '../components/spec/fallback-states.js';
import { LaneBoard, type Lane, type LaneCard } from '../components/spec/LaneBoard.js';
import { LaneSelectionBar, type LaneSelectionChip } from '../components/spec/LaneSelectionBar.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import { useBusinessMap } from '../hooks/queries/spec-graph-lanes-queries.js';
import {
  usePjCharter,
  usePjLaneChanges,
  usePjScreenChanges,
} from '../hooks/queries/spec-graph-element-pj-queries.js';
import { useListScreens } from '../hooks/queries/spec-graph-element-screens-queries.js';
import { formatPjLabel } from '../lib/pj-label.js';
import { flowKindLabel, laneCardHint, untranslatedHint } from './lane-card-hints.js';
import {
  businessCardDetail,
  flowCardDetail,
  ruleCardDetail,
  screenCardDetail,
  stageCardDetail,
} from './lane-card-details.js';
import { orderFlowsByStage } from './overview-selection.js';
import { PjCharterSection } from './pj-charter-section.js';
import { buildPjLaneMarkIndex, pjLaneMarkOf } from './pj-lane-marks.js';
import {
  buildPjTouchedByType,
  clearPjLanesSelection,
  withoutDescendantPjLaneChanges,
  EMPTY_PJ_LANES_SELECTION,
  laneViewOf,
  PJ_LANES_SELECTION_LANES,
  pjScreenItemsOf,
  pjStageOfFlowIndex,
  selectPjLanes,
  togglePjLanesSelection,
  type PjLaneView,
  type PjLanesSelection,
  type PjLanesSelectionLane,
  type PjScreenLabel,
} from './pj-lanes-build.js';
import { PjRequirementWhyModal } from './pj-lanes-sections.js';

/** 文言を引く関数。埋め込みを持つ鍵もあるので、引数つきで受ける。 @graph-connects none */
type Translate = (key: string, params?: Record<string, unknown>) => string;

/** 絞り込みの札で要求の文を切り詰める長さ（合意 `pj-changes.html` の「先頭 18 字…」）。 @graph-connects none */
const REQUIREMENT_CHIP_LENGTH = 18;

/**
 * `PjLanesPage` の props。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ④PJ の変更一覧の入力（案件番号・案件の変更・要求）
 * @graph-connects none
 */
export interface PjLanesPageProps {
  /** いま見ている案件の番号 */
  pjNumber: number;
  /** その案件の変更の行（`getChangeHistory`）。印の出どころはこの 1 本である */
  pjChanges: readonly PjChangeRow[];
  /** その案件が持つ要求（`listPjRequirements`） */
  requirements: readonly PjRequirement[];
}

/**
 * 案件の状態を変更の行から読む。1 行でも確定済みなら反映済み。
 *
 * @param rows 案件の変更の行
 * @returns 反映済みなら true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更の行から、案件が反映済みかを読む。
 * @graph-connects none
 */
export function isPjMerged(rows: readonly PjChangeRow[]): boolean {
  return rows.some((row) => row.status === 'merged');
}

/**
 * PJ の変更一覧のページ。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の憲章・案件を当てた姿・master の姿・変わったレーン 4 型・変わった画面・画面の名前を取得し、見出し・その下の PJ 憲章の中身・変更のレーン列を描く。
 * @graph-connects usePjCharter [calls] 案件の憲章の取得
 * @graph-connects PjCharterSection [calls] 見出しの下の PJ 憲章の中身
 * @graph-connects useBusinessMap [calls] 案件を当てた姿と master の姿の取得
 * @graph-connects usePjLaneChanges [calls] 変わったレーン 4 型の取得
 * @graph-connects usePjScreenChanges [calls] 部品を持ち主に寄せた、案件で変わった画面の取得
 * @graph-connects useListScreens [calls] 画面の名前（案件を当てた姿と master）の取得
 */
export function PjLanesPage({ pjNumber, pjChanges, requirements }: PjLanesPageProps) {
  const { t } = useTranslation();
  const charter = usePjCharter(pjNumber);
  const after = useBusinessMap(pjNumber);
  const master = useBusinessMap();
  const laneChanges = usePjLaneChanges(pjNumber);
  const screenChanges = usePjScreenChanges(pjNumber);
  const screensAfter = useListScreens(pjNumber);
  const screensMaster = useListScreens();
  const merged = isPjMerged(pjChanges);

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center gap-3">
        <h1
          data-pj-state={merged ? 'merged' : 'open'}
          title={merged ? t('pjs.stateMerged') : t('pjs.stateOpen')}
          className="text-2xl font-bold text-[var(--color-text-primary)]"
        >
          {t('pjLanes.title', { pj: formatPjLabel(pjNumber) })}
        </h1>
      </header>
      <PjCharterSection data={charter.data} error={charter.error} />
      <PjLanesBody
        after={after}
        master={master}
        laneChanges={laneChanges}
        screenChanges={screenChanges}
        screensAfter={screensAfter}
        screensMaster={screensMaster}
        pjNumber={pjNumber}
        requirements={requirements}
      />
    </div>
  );
}

interface PjLanesBodyProps extends Omit<PjLanesPageProps, 'pjChanges'> {
  after: ReturnType<typeof useBusinessMap>;
  master: ReturnType<typeof useBusinessMap>;
  laneChanges: ReturnType<typeof usePjLaneChanges>;
  screenChanges: ReturnType<typeof usePjScreenChanges>;
  screensAfter: ReturnType<typeof useListScreens>;
  screensMaster: ReturnType<typeof useListScreens>;
}

/**
 * 本体（失敗 / 読み込み中 / レーン列）の出し分け。
 *
 * 6 本の読取のどれかが失敗したら失敗を出す。**揃わないまま描かない** — master が来ていない
 * と削除された要素が引けず、消える要素だけが画面から抜け落ちる。画面の名前も同じで、片方の
 * 画面一覧が来ないうちに描くと、案件が消した画面だけが「未設定」で出る。
 *
 * **案件が変わったら選択を捨てる。** 要素の id はグラフ全体で 1 本の連番なので、前の案件で
 * 選んだ id は次の案件のレーンでも実在の id として効いてしまう。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 6 本の読取を失敗・読み込み中・本体の 3 状態で出し分け、案件が変わったら選択を捨てる。
 * @graph-connects PjLanesBoard [calls] レーン列と絞り込みの描画
 * @graph-connects pjScreenItemsOf [calls] 案件で変わった画面に名前を付ける
 */
function PjLanesBody({
  after,
  master,
  laneChanges,
  screenChanges,
  screensAfter,
  screensMaster,
  pjNumber,
  requirements,
}: PjLanesBodyProps) {
  const { t } = useTranslation();
  const afterView = useMemo(
    () => (after.data === undefined ? undefined : laneViewOf(after.data)),
    [after.data],
  );
  const masterView = useMemo(
    () => (master.data === undefined ? undefined : laneViewOf(master.data)),
    [master.data],
  );
  const error =
    after.error ??
    master.error ??
    laneChanges.error ??
    screenChanges.error ??
    screensAfter.error ??
    screensMaster.error;

  if (error !== null && error !== undefined) {
    return <ErrorState error={error} />;
  }

  if (
    afterView === undefined ||
    masterView === undefined ||
    laneChanges.data === undefined ||
    screenChanges.data === undefined ||
    screensAfter.data === undefined ||
    screensMaster.data === undefined
  ) {
    return (
      <div aria-busy="true" aria-label={t('common.loading')}>
        <Skeleton variant="rectangular" height="620px" />
      </div>
    );
  }

  return (
    <PjLanesBoard
      key={pjNumber}
      after={afterView}
      master={masterView}
      pjNumber={pjNumber}
      laneChanges={laneChanges.data}
      screenChanges={screenChanges.data}
      screenLabelsAfter={screensAfter.data.screens}
      screenLabelsMaster={screensMaster.data.screens}
      requirements={requirements}
    />
  );
}

/**
 * `PjLanesBoard` の props。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 6 レーンの入力（2 つの姿・案件番号・変わったレーン 4 型・変わった画面と画面の名前・要求）
 * @graph-connects none
 */
export interface PjLanesBoardProps {
  /** 案件を当てた姿 */
  after: PjLaneView;
  /** master の姿 */
  master: PjLaneView;
  /** いま見ている案件の番号 */
  pjNumber: number;
  /** その案件で変わったレーン 4 型（直接と配下。共通部が決めたもの。配下に変更はここで外す） */
  laneChanges: readonly PjLaneChange[];
  /** その案件で変わった画面（部品は持ち主に寄せたもの。共通部が決めたもの） */
  screenChanges: readonly PjScreenChange[];
  /** 画面の名前（案件を当てた姿の画面一覧） */
  screenLabelsAfter: readonly PjScreenLabel[];
  /** 画面の名前（master の画面一覧。案件が消した画面の名前はここにしか無い） */
  screenLabelsMaster: readonly PjScreenLabel[];
  /** その案件が持つ要求 */
  requirements: readonly PjRequirement[];
}

/** 副のボタン「背景」の見た目（枠だけ。主のボタンと見分ける）。 @graph-connects none */
const BACKGROUND_BUTTON_CLASS =
  'inline-flex h-[22px] items-center gap-1 rounded-sm border border-[var(--color-border)] bg-[var(--color-bg-base)] px-2 text-[11px] font-bold leading-none text-[var(--color-text-secondary)] hover:border-[var(--color-border-hover)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)]';

/**
 * 絞り込みの札・6 レーン・下の段・要求の背景のモーダル。**選択と開いている背景はこの部品が持つ。**
 *
 * 背景を開いても選択は動かさない。背景のボタンは要求のカードの押せる本体の外にあるので、押しても
 * 絞り込みは起きない。閉じたら押したボタンへフォーカスを戻す — 戻さないとキーボードで読んでいた
 * 位置を見失う。
 *
 * 画面のレーンは業務ルールの右に置く。一番右なのでカードは押せず（絞る先が無い）、画面詳細へは
 * 右上の「詳細」でだけ行く。業務ルールのカードも絞る先が無いので同じく押せず、業務ルールの画面へは
 * 「詳細」でだけ行く（Dhik 2026-09-30「詳細を見るときには詳細ボタンをクリックに統一して」）。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ④PJ の変更一覧の選択と開いている要求の背景を保ち、絞り込みの札と 6 レーンの看板形式のカード列と背景のモーダルを描く。
 * @graph-connects LaneBoard [calls] レーン列の描画
 * @graph-connects withoutDescendantPjLaneChanges [calls] 4 レーンの中身と印を直接の変更だけに絞る
 * @graph-connects selectPjLanes [calls] 案件が触った要素だけの 5 レーンの中身
 * @graph-connects pjScreenItemsOf [calls] 案件で変わった画面に名前を付ける
 * @graph-connects orderFlowsByStage [calls] 業務フローを業務マップと同じ並びにする
 * @graph-connects stageCardDetail [calls] ステージのカードの「詳細」の行き先
 * @graph-connects flowCardDetail [calls] 業務フローのカードの「詳細」の行き先
 * @graph-connects businessCardDetail [calls] 業務のカードの「詳細」の行き先
 * @graph-connects ruleCardDetail [calls] 業務ルールのカードの「詳細」の行き先
 * @graph-connects screenCardDetail [calls] 画面のカードの「詳細」の行き先
 * @graph-connects flowKindLabel [calls] 業務フローの型の呼び名
 * @graph-connects laneCardHint [calls] カードの補足を 1 行にする
 * @graph-connects untranslatedHint [calls] 名前はあるが訳の欠けたカードの「未翻訳」
 * @graph-connects PjRequirementWhyModal [calls] 要求の背景のモーダル
 */
export function PjLanesBoard({
  after,
  master,
  pjNumber,
  laneChanges,
  screenChanges,
  screenLabelsAfter,
  screenLabelsMaster,
  requirements,
}: PjLanesBoardProps) {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<PjLanesSelection>(EMPTY_PJ_LANES_SELECTION);
  const [whyOpen, setWhyOpen] = useState<number | undefined>(undefined);
  const whyTrigger = useRef<HTMLButtonElement | null>(null);
  /* レーンの中身と印は同じ 1 本から引く。片方だけ絞ると、出ないはずのカードに印が残る。 */
  const direct = useMemo(() => withoutDescendantPjLaneChanges(laneChanges), [laneChanges]);
  const touched = useMemo(() => buildPjTouchedByType(direct), [direct]);
  const markIndex = useMemo(() => buildPjLaneMarkIndex(direct), [direct]);
  const stageOfFlow = useMemo(() => pjStageOfFlowIndex({ after, master }), [after, master]);
  const screens = useMemo(
    () => pjScreenItemsOf(screenChanges, screenLabelsAfter, screenLabelsMaster),
    [screenChanges, screenLabelsAfter, screenLabelsMaster],
  );
  const lanes = useMemo(
    () => selectPjLanes({ after, master, touched, screens, requirements, selection }),
    [after, master, touched, screens, requirements, selection],
  );
  /*
   * 業務フローの並びは業務マップと同じ決まりにする。ステージの順は、案件を当てた姿の鎖の順に
   * master にしか居ないステージを続けたもの。
   */
  const flows = useMemo(
    () =>
      orderFlowsByStage(lanes.flows, [
        ...new Set([...after.stages, ...master.stages].map((stage) => stage.stageId)),
      ]),
    [lanes.flows, after.stages, master.stages],
  );
  const openRequirement = requirements.find((item) => item.requirementId === whyOpen);

  const select = (lane: PjLanesSelectionLane) => (id: number) => {
    setSelection((current) => togglePjLanesSelection(current, lane, id, stageOfFlow));
  };

  /*
   * 見出しが空のカードは「未設定」「未翻訳」を主表記にする。id に落とすと人が判別できない。名前はあるが
   * 訳の欠けたカードの「未翻訳」は、ほかの補足と一緒にカードの title に載せる。
   */
  const card = (
    id: number,
    label: string,
    untranslated: boolean,
    rest: Partial<LaneCard>,
    hints: readonly (string | null | undefined)[] = [],
  ) =>
    ({
      id,
      title: displayLabel(label, t, untranslated),
      hint: laneCardHint([untranslatedHint(label, untranslated, t), ...hints]),
      diff: pjLaneMarkOf(markIndex, id),
      ...rest,
    }) satisfies LaneCard;

  const board: readonly Lane[] = [
    {
      key: 'requirement',
      title: t('pjLanes.laneRequirement'),
      count: requirements.length,
      selectedId: selection.requirementId,
      onSelect: select('requirement'),
      cards: requirements.map((requirement) => ({
        id: requirement.requirementId,
        title: truncateRequirementStatement(requirement.statement),
        diff: 'unchanged' as const,
        action: (
          <button
            type="button"
            aria-haspopup="dialog"
            data-requirement-background={requirement.requirementId}
            className={BACKGROUND_BUTTON_CLASS}
            onClick={(event) => {
              whyTrigger.current = event.currentTarget;
              setWhyOpen(requirement.requirementId);
            }}
          >
            {t('pjLanes.requirementBackground')}
          </button>
        ),
      })),
    },
    {
      key: 'stage',
      title: t('pjLanes.laneStage'),
      count: lanes.stages.length,
      selectedId: selection.stageId,
      onSelect: select('stage'),
      cards: lanes.stages.map((stage) =>
        card(stage.stageId, stage.label, stage.untranslated, {
          detail: stageCardDetail(stage.stageId, pjNumber, t),
        }),
      ),
    },
    {
      key: 'flow',
      title: t('pjLanes.laneFlow'),
      count: flows.length,
      selectedId: selection.flowId,
      onSelect: select('flow'),
      cards: flows.map((flow) =>
        card(
          flow.flowId,
          flow.label,
          flow.untranslated,
          { detail: flowCardDetail(flow.flowId, pjNumber, t) },
          [flowKindLabel(flow.role, t)],
        ),
      ),
    },
    {
      key: 'business',
      title: t('pjLanes.laneBusiness'),
      count: lanes.businesses.length,
      selectedId: selection.businessId,
      onSelect: select('business'),
      cards: lanes.businesses.map((business) =>
        card(
          business.businessId,
          business.label,
          business.untranslated,
          { detail: businessCardDetail(business.businessId, pjNumber, t) },
          [business.domain?.label],
        ),
      ),
    },
    {
      key: 'rule',
      title: t('pjLanes.laneRule'),
      count: lanes.rules.length,
      cards: lanes.rules.map((rule) =>
        card(rule.ruleId, rule.label, rule.untranslated, {
          detail: ruleCardDetail(rule.ruleId, pjNumber, t),
        }),
      ),
    },
    {
      key: 'screen',
      title: t('pjLanes.laneScreen'),
      count: lanes.screens.length,
      cards: lanes.screens.map((item) => ({
        id: item.screenId,
        title: displayLabel(item.label, t, item.untranslated),
        hint: laneCardHint([untranslatedHint(item.label, item.untranslated, t)]),
        diff: item.mark,
        detail: screenCardDetail(item.screenId, pjNumber, t),
      })),
    },
  ];

  return (
    <div className="flex flex-col">
      <LaneSelectionBar
        chips={pjSelectionChips({ after, master, requirements, selection }, t)}
        onClear={(key) => {
          setSelection((current) => clearPjLanesSelection(current, toSelectionLane(key)));
        }}
        onClearAll={() => {
          setSelection(EMPTY_PJ_LANES_SELECTION);
        }}
      />
      <LaneBoard lanes={board} countInTitle controlsOnHover />
      {openRequirement !== undefined && (
        <PjRequirementWhyModal
          requirement={openRequirement}
          onClose={() => {
            setWhyOpen(undefined);
            whyTrigger.current?.focus();
          }}
        />
      )}
    </div>
  );
}

/** 札の鍵をレーンの種別に読み替える。閉集合に無い鍵は要求として扱う。 @graph-connects none */
function toSelectionLane(key: string): PjLanesSelectionLane {
  return PJ_LANES_SELECTION_LANES.find((lane) => lane === key) ?? 'requirement';
}

/**
 * `pjSelectionChips` の入力。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 絞り込みの札を組むための入力（2 つの姿・要求・いまの選択）
 * @graph-connects none
 */
export interface PjSelectionChipsInput {
  after: PjLaneView;
  master: PjLaneView;
  requirements: readonly PjRequirement[];
  selection: PjLanesSelection;
}

/**
 * 要求の文を札に載る長さへ切り詰める。
 *
 * 字の数え方はカードの見出しと書込の上限に揃える（共通部の `truncateRequirementStatement`。書記素で
 * 数える）。札だけ別の数え方で切ると、同じ文が札とカードで違う位置で切れる。
 *
 * @param statement 要求の文
 * @returns 18 字を超えれば先頭 18 字と「…」
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 要求の文を絞り込みの札に載る長さへ切り詰める。
 * @graph-connects truncateRequirementStatement [calls] 書記素単位の切り詰め
 */
export function shortRequirement(statement: string): string {
  return truncateRequirementStatement(statement, REQUIREMENT_CHIP_LENGTH);
}

/**
 * いま絞っている条件を札に写す（要求 › ステージ › 業務フロー › 業務 の順）。
 *
 * 見出しは 2 つの姿から引く。案件が消した要素を選んでいるときは案件を当てた姿に居ないので、
 * 案件を当てた姿だけを見ると札が空になる。
 *
 * @param input 2 つの姿・要求・いまの選択
 * @param t 文言
 * @returns 札の並び（左のレーンから順）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business いまの選択を、レーンの見出しとカードの見出しを持つ札の並びへ写す。
 * @graph-connects shortRequirement [calls] 要求の文の切り詰め
 */
export function pjSelectionChips(
  input: PjSelectionChipsInput,
  t: Translate,
): readonly LaneSelectionChip[] {
  const { after, master, requirements, selection } = input;
  const chips: LaneSelectionChip[] = [];

  const requirement = requirements.find((item) => item.requirementId === selection.requirementId);
  if (requirement !== undefined) {
    chips.push({
      key: 'requirement',
      lane: t('pjLanes.laneRequirement'),
      label: shortRequirement(requirement.statement),
    });
  }
  const stage = [...after.stages, ...master.stages].find(
    (item) => item.stageId === selection.stageId,
  );
  if (stage !== undefined) {
    chips.push({
      key: 'stage',
      lane: t('pjLanes.laneStage'),
      label: displayLabel(stage.label, t, stage.untranslated),
    });
  }
  const flow = [...after.flows, ...master.flows].find((item) => item.flowId === selection.flowId);
  if (flow !== undefined) {
    chips.push({
      key: 'flow',
      lane: t('pjLanes.laneFlow'),
      label: displayLabel(flow.label, t, flow.untranslated),
    });
  }
  const business = [...after.businesses, ...master.businesses].find(
    (item) => item.businessId === selection.businessId,
  );
  if (business !== undefined) {
    chips.push({
      key: 'business',
      lane: t('pjLanes.laneBusiness'),
      label: displayLabel(business.label, t, business.untranslated),
    });
  }
  return chips;
}
